import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { formatUSPhone } from "@/lib/formatters";
import useLeadCapture from "@/hooks/useLeadCapture";
import { trackLead } from "@/lib/metaPixel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DOCTOR_IMG = "https://images.unsplash.com/photo-1758691462858-f1286e5daf40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxkb2N0b3IlMjBjb25zdWx0aW5nJTIwcGF0aWVudCUyMGNvbXBhc3Npb258ZW58MHx8fHwxNzc2MTAwNjk0fDA&ixlib=rb-4.1.0&q=85";

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    goal: "",
    preferred_date: "",
    plan: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Real-time progressive lead capture
  const { capture: captureLead } = useLeadCapture("contact");
  useEffect(() => {
    // The "name" field is a single field in this form — split best-effort
    // so the back-end has first/last separately when possible.
    const parts = (form.name || "").trim().split(/\s+/);
    captureLead({
      first_name: parts[0] || "",
      last_name: parts.length > 1 ? parts.slice(1).join(" ") : "",
      email: form.email,
      phone: form.phone,
      goal: form.goal,
    });
  }, [form.name, form.email, form.phone, form.goal, captureLead]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleGoalChange = (value) => {
    setForm({ ...form, goal: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.goal) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consultation`, form);
      setSubmitted(true);
      toast.success("Consultation booked successfully! We'll be in touch soon.");
      trackLead({
        content_name: "Landing Contact",
        form_id: "landing-contact",
        email: form.email,
        phone: form.phone,
      });
      setForm({ name: "", email: "", phone: "", goal: "", preferred_date: "", plan: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#EFECE5]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs md:text-sm font-sans font-medium uppercase tracking-[0.2em] text-[#C47C5D] mb-4 block">
              Start Your Journey
            </span>
            <h2 className="text-4xl md:text-5xl font-serif font-light tracking-tight text-[#2A3324] mb-4">
              Qualify for a Free Doctor Discussion
            </h2>
            <p className="text-base font-sans font-light text-[#2A3324]/60 mb-10">
              Take the quiz above to get your personalized peptide protocol, or fill out this form
              and our team will reach out within 24 hours to schedule your free consultation.
            </p>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="consultation-success"
                className="bg-white rounded-[2rem] p-10 text-center border border-[#5C715E]/20"
              >
                <div className="w-16 h-16 bg-[#5C715E]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Send size={28} className="text-[#5C715E]" />
                </div>
                <h3 className="text-2xl font-serif font-normal text-[#2A3324] mb-2">
                  You're All Set!
                </h3>
                <p className="text-base font-sans font-light text-[#2A3324]/60">
                  Our team will contact you within 24 hours. Check your email for
                  confirmation details.
                </p>
                <button
                  data-testid="book-another-btn"
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-sm font-sans font-medium text-[#5C715E] underline underline-offset-4 hover:text-[#4A5A4B]"
                >
                  Book another consultation
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="consultation-form" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-2 block">
                      Full Name *
                    </label>
                    <input
                      data-testid="form-name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                      className="underline-input w-full"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-2 block">
                      Email *
                    </label>
                    <input
                      data-testid="form-email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      className="underline-input w-full"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-2 block">
                      Phone
                    </label>
                    <input
                      data-testid="form-phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: formatUSPhone(e.target.value) })}
                      placeholder="(555) 000-0000"
                      maxLength={17}
                      className="underline-input w-full"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-2 block">
                      Preferred Date
                    </label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          data-testid="form-date"
                          type="button"
                          className="underline-input w-full text-left flex items-center justify-between"
                        >
                          <span className={form.preferred_date ? "text-[#2A3324]" : "text-[#2A3324]/40"}>
                            {form.preferred_date
                              ? format(new Date(form.preferred_date), "MMMM d, yyyy")
                              : "Select a date"}
                          </span>
                          <CalendarIcon size={16} strokeWidth={1.5} className="text-[#2A3324]/40" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={form.preferred_date ? new Date(form.preferred_date) : undefined}
                          onSelect={(date) =>
                            setForm({ ...form, preferred_date: date ? format(date, "yyyy-MM-dd") : "" })
                          }
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-2 block">
                    Primary Goal *
                  </label>
                  <Select onValueChange={handleGoalChange} value={form.goal}>
                    <SelectTrigger
                      data-testid="form-goal"
                      className="underline-input w-full border-0 border-b border-[#2A3324]/20 rounded-none px-0 py-3 h-auto shadow-none ring-0 focus:ring-0"
                    >
                      <SelectValue placeholder="Select your primary health goal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weight-loss">Weight Management</SelectItem>
                      <SelectItem value="anti-aging">Anti-Aging & Rejuvenation</SelectItem>
                      <SelectItem value="recovery">Muscle Recovery & Healing</SelectItem>
                      <SelectItem value="cognitive">Cognitive Enhancement</SelectItem>
                      <SelectItem value="immune">Immune Support</SelectItem>
                      <SelectItem value="sexual-wellness">Sexual Wellness</SelectItem>
                      <SelectItem value="general">General Wellness</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <button
                  type="submit"
                  data-testid="submit-consultation-btn"
                  disabled={loading}
                  className="bg-[#5C715E] text-[#F7F5F0] hover:bg-[#4A5A4B] rounded-full px-10 py-4 text-base font-sans font-medium tracking-wide transition-all duration-300 hover:-translate-y-1 shadow-sm inline-flex items-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed mt-4"
                >
                  {loading ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Send size={18} strokeWidth={1.5} />
                  )}
                  {loading ? "Submitting..." : "Book My Consultation"}
                </button>
              </form>
            )}
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:block"
          >
            <div className="rounded-[2rem] overflow-hidden aspect-[4/5]">
              <img
                src={DOCTOR_IMG}
                alt="Doctor consulting with patient"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
